import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { surveyAPI, operationAPI } from '../services/api'
import SurveyReportsModal from '../components/SurveyReportsModal'
import PageHeader from '../components/PageHeader'

const SurveyList = () => {
  const { isStaff, isSuperuser } = useAuth()
  const navigate = useNavigate()
  const [surveys, setSurveys] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchCategory, setSearchCategory] = useState('all')
  const [showCalendarOnly, setShowCalendarOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [calendarLoading, setCalendarLoading] = useState({})
  const [expandedCards, setExpandedCards] = useState(new Set())
  const [showFilterFrame, setShowFilterFrame] = useState(false)
  const [reportsModalRef, setReportsModalRef] = useState(null)

  useEffect(() => {
    if (!isStaff) {
      navigate('/operations')
      return
    }
    fetchSurveys()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, page, searchQuery, searchCategory, showCalendarOnly])

  const fetchSurveys = async () => {
    setLoading(true)
    try {
      const params = {
        page,
        search: searchQuery,
        category: searchCategory,
      }
      if (showCalendarOnly) {
        params.calendar = 'true'
      }
      const response = await surveyAPI.list(params)
      setSurveys(response.data.results)
      setTotalPages(response.data.total_pages)
    } catch (error) {
      console.error('Error fetching surveys:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleCard = (referenceNumber) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(referenceNumber)) {
        newSet.delete(referenceNumber)
      } else {
        newSet.add(referenceNumber)
      }
      return newSet
    })
  }

  const handleToggleStatus = async (id, e) => {
    e?.stopPropagation()
    if (!window.confirm(`Are you sure you want to ${surveys.find(s => s.reference_number === id)?.cancelled ? 'activate' : 'cancel'} this survey?`)) {
      return
    }
    try {
      await surveyAPI.toggleStatus(id)
      await fetchSurveys()
    } catch (error) {
      console.error('Error toggling status:', error)
      alert(error.response?.data?.message || 'Failed to toggle status')
    }
  }

  const handleToggleInvoiceSent = async (referenceNumber, invoiceSent, e) => {
    e?.stopPropagation()
    if (!window.confirm(`Are you sure you want to mark this invoice as ${invoiceSent ? 'sent' : 'not sent'}?`)) {
      return
    }
    try {
      const response = await surveyAPI.toggleInvoiceSent(referenceNumber, invoiceSent)
      if (response.data?.success) {
        await fetchSurveys()
      } else {
        alert(response.data?.message || 'Failed to update invoice status')
      }
    } catch (error) {
      console.error('Error toggling invoice sent:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Failed to update invoice status'
      alert(errorMessage)
    }
  }

  const handleAddToCalendar = async (referenceNumber, e) => {
    e?.stopPropagation()
    setCalendarLoading(prev => ({ ...prev, [referenceNumber]: true }))
    try {
      await operationAPI.createFromSurvey(referenceNumber)
      await fetchSurveys()
    } catch (error) {
      console.error('Error adding to calendar:', error)
      alert(error.response?.data?.message || 'Failed to add survey to calendar')
    } finally {
      setCalendarLoading(prev => ({ ...prev, [referenceNumber]: false }))
    }
  }

  const handleRemoveFromCalendar = async (referenceNumber, e) => {
    e?.stopPropagation()
    setCalendarLoading(prev => ({ ...prev, [referenceNumber]: true }))
    try {
      await operationAPI.deleteByRef(referenceNumber)
      await fetchSurveys()
    } catch (error) {
      console.error('Error removing from calendar:', error)
      alert(error.response?.data?.message || 'Failed to remove survey from calendar')
    } finally {
      setCalendarLoading(prev => ({ ...prev, [referenceNumber]: false }))
    }
  }

  // eslint-disable-next-line no-unused-vars
  const _formatDate = (dateString) => {
    if (!dateString) return '-'
    
    // Parse date string as date components to avoid timezone conversion issues
    // Backend sends dates as YYYY-MM-DD strings which should be timezone-neutral
    let year, month, day
    
    if (typeof dateString === 'string') {
      // Extract YYYY-MM-DD from string (handle both pure date strings and ISO datetime strings)
      const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (dateMatch) {
        year = parseInt(dateMatch[1], 10)
        month = parseInt(dateMatch[2], 10) - 1 // Month is 0-indexed
        day = parseInt(dateMatch[3], 10)
      } else {
        // Fallback: parse as Date and extract components in Europe/London timezone
        const date = new Date(dateString)
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        const parts = formatter.format(date).split('-')
        year = parseInt(parts[0], 10)
        month = parseInt(parts[1], 10) - 1
        day = parseInt(parts[2], 10)
      }
    } else {
      // If it's already a Date object, extract components in Europe/London timezone
      const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
      const parts = formatter.format(dateString).split('-')
      year = parseInt(parts[0], 10)
      month = parseInt(parts[1], 10) - 1
      day = parseInt(parts[2], 10)
    }
    
    // Create a date object with the parsed components and format it
    // Using local date constructor avoids timezone conversion
    const date = new Date(year, month, day)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    })
  }

  // eslint-disable-next-line no-unused-vars
  const _formatCurrency = (value) => {
    if (value === null || value === undefined) return '-'
    return `$${parseFloat(value).toFixed(2)}`
  }

  // eslint-disable-next-line no-unused-vars
  const _formatFloat = (value, decimals = 3) => {
    if (value === null || value === undefined) return '-'
    return parseFloat(value).toFixed(decimals)
  }

  if (!isStaff) return null

  return (
    <>
    <div className="min-h-screen bg-[#faf8f6]">
      <div className="page-container">
        {/* Header */}
        <PageHeader title="KBL Surveys" description="Manage and track survey operations">
          <button
            onClick={() => setShowFilterFrame(!showFilterFrame)}
            className={`p-2 rounded-lg border transition-colors ${showFilterFrame ? 'border-[#ea580c] bg-[#fff7ed] text-[#c2410c]' : 'border-[#efe3d6] bg-white text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed]'}`}
            title="Toggle Filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
          <button
            onClick={() => navigate('/surveys/new')}
            className="bg-premium inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(234,88,12,0.35)] transition-shadow hover:shadow-[0_6px_20px_rgba(234,88,12,0.45)]"
          >
            <i className="bi bi-plus-lg"></i>
            New Survey
          </button>
        </PageHeader>

        {/* Search and Filters */}
        {showFilterFrame && (
          <div className="bg-white rounded-xl border border-[#efe3d6] shadow-[0_1px_6px_rgba(234,88,12,0.12)] overflow-hidden mb-6">
            <div className="table-head-premium px-5 py-2">
              <h3 className="text-sm font-semibold flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-white" aria-hidden="true">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                  </span>
                  <span>Filter Options</span>
                </div>
                <button
                  onClick={() => setShowFilterFrame(false)}
                  className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-all duration-200 hover:scale-110 active:scale-95"
                  title="Close Filters"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </h3>
            </div>
            <div className="p-4 md:p-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <select
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white min-w-[180px]"
                >
                  <option value="all">🔍 All Fields</option>
                  <option value="client">👤 Client</option>
                  <option value="vessel">🚢 Vessel</option>
                  <option value="surveyor">👤 Surveyor</option>
                  <option value="port">⚓ Port</option>
                  <option value="country">🌍 Country</option>
                  <option value="reference">🔢 Reference</option>
                </select>
                <div className="flex-1 relative">
                  <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search surveys..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white"
                  />
                </div>
                <label className="flex items-center px-3 py-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showCalendarOnly}
                    onChange={(e) => {
                      setShowCalendarOnly(e.target.checked)
                      setPage(1)
                    }}
                    className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500 mr-2"
                  />
                  Calendar Events Only
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Survey Cards */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#ea580c]"></div>
            <p className="text-gray-600 mt-4 text-sm">Loading surveys...</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-sm">No surveys found</p>
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="table-head-premium rounded-xl px-4 py-3 mb-3 shadow-[0_1px_6px_rgba(234,88,12,0.3)]">
              <div className="grid grid-cols-12 gap-3 text-xs text-white uppercase tracking-wide font-semibold">
                <div className="col-span-2">
                  <span>Client</span>
                </div>
                <div className="col-span-2 pl-2">
                  <span>Survey Type</span>
                </div>
                <div className="col-span-2 pl-2">
                  <span>Vessel</span>
                </div>
                <div className="col-span-2 pl-2 pr-0">
                  <span>Country</span>
                </div>
                <div className="col-span-2 pl-0 pr-0">
                  <span>Port</span>
                </div>
                <div className="col-span-2 pl-0 pr-0 min-w-[100px] flex justify-end">
                  <span>Ref & Actions</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {surveys.map((survey, index) => {
                const isExpanded = expandedCards.has(survey.reference_number)
                const isLoading = calendarLoading[survey.reference_number]
                const hasOperation = survey.has_operation
                const hasReports = survey.has_reports
                const invoiceSent = survey.invoice_sent === 'Yes'
                const hasReceivedPayment = Boolean(
                  survey.received_payment_date && String(survey.received_payment_date).trim()
                )
                const isCancelled = survey.cancelled
                const lastFourRef = `#${survey.reference_number.slice(-4)}`
                const country = survey.survey_country

                return (
                  <div
                    key={survey.reference_number}
                    className={`group transition-all duration-300 rounded-xl shadow-lg hover:shadow-2xl border-2 border-gray-200 overflow-hidden bg-white ${
                      hasReports ? 'border-t-[5px] border-t-[#ea580c]' : ''
                    } ${
                      invoiceSent
                        ? 'border-l-[8px] border-l-emerald-500'
                        : 'border-l-[8px] border-l-rose-500'
                    } ${
                      hasReceivedPayment ? 'border-r-[8px] border-r-amber-400' : ''
                    } ${isCancelled ? 'opacity-70 grayscale-[0.3]' : ''} ${isExpanded ? 'ring-2 ring-orange-500/50 shadow-2xl' : ''}`}
                  >
                    {/* Card Header - Enhanced */}
                    <div
                      className={`px-3 md:pl-5 md:pr-6 py-1 md:py-0.5 cursor-pointer transition-all duration-200 border-b border-gray-200/50 hover:bg-gray-50`}
                      onClick={() => toggleCard(survey.reference_number)}
                    >
                      <div className="grid grid-cols-12 gap-3 items-center text-sm">
                        <div className="col-span-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base flex-shrink-0 ${isCancelled ? 'text-pink-300' : 'text-orange-500'}`}>👤</span>
                            <span className={`truncate whitespace-nowrap ${isCancelled ? 'text-pink-300' : 'text-gray-900'}`} title={survey.client_name}>{survey.client_name}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base flex-shrink-0 ${isCancelled ? 'text-pink-300' : 'text-orange-500'}`}>📋</span>
                            <span className={`truncate whitespace-nowrap ${isCancelled ? 'text-pink-300' : 'text-gray-900'}`} title={survey.survey_type}>{survey.survey_type}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base flex-shrink-0 ${isCancelled ? 'text-pink-300' : 'text-orange-500'}`}>🚢</span>
                            <span className={`truncate whitespace-nowrap ${isCancelled ? 'text-pink-300' : 'text-gray-900'}`} title={survey.vessel_name}>{survey.vessel_name}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-2 pr-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base flex-shrink-0 ${isCancelled ? 'text-pink-300' : 'text-orange-500'}`}>🌍</span>
                            <span className={`truncate whitespace-nowrap ${isCancelled ? 'text-pink-300' : 'text-gray-900'}`} title={country}>{country}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-0 pr-0">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base flex-shrink-0 ${isCancelled ? 'text-pink-300' : 'text-orange-500'}`}>⚓</span>
                            <span className={`truncate whitespace-nowrap ${isCancelled ? 'text-pink-300' : 'text-gray-900'}`} title={survey.survey_port}>{survey.survey_port}</span>
                          </div>
                        </div>
                        <div className="col-span-2 pl-0 pr-0 min-w-[100px] flex justify-end items-center gap-6 -mr-2">
                          <div className="flex items-center gap-1 -ml-4">
                            <span className={`truncate whitespace-nowrap text-sm font-bold ${isCancelled ? 'text-pink-400' : 'text-orange-600'}`}>{lastFourRef}</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                            {/* Invoice Button (Superuser only) */}
                            {isSuperuser && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/surveys/${survey.reference_number}/invoice`)
                                }}
                                className="p-1.5 rounded-lg transition-all duration-200 flex-shrink-0 hover:scale-110 active:scale-95"
                                title="Invoice"
                              >
                                <span className="text-2xl">🧾</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/surveys/${survey.reference_number}/edit`)
                              }}
                              className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110 active:scale-95"
                              title="Edit Survey"
                            >
                              <span className="text-2xl">✏️</span>
                            </button>
                            <button
                              onClick={(e) => handleRemoveFromCalendar(survey.reference_number, e)}
                              disabled={isLoading || !hasOperation}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                hasOperation
                                  ? 'bg-pink-100 hover:bg-pink-200 text-pink-700 hover:text-pink-800 hover:scale-110 active:scale-95'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Remove from Calendar"
                            >
                              {isLoading && hasOperation ? (
                                <i className="bi bi-arrow-clockwise animate-spin text-lg"></i>
                              ) : (
                                <i className="bi bi-calendar-minus-fill text-xl"></i>
                              )}
                            </button>
                            <button
                              onClick={(e) => handleAddToCalendar(survey.reference_number, e)}
                              disabled={isLoading || hasOperation}
                              className={`p-2 rounded-lg transition-all duration-200 ${
                                !hasOperation
                                  ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700 hover:text-emerald-800 hover:scale-110 active:scale-95'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Add to Calendar"
                            >
                              {isLoading && !hasOperation ? (
                                <i className="bi bi-arrow-clockwise animate-spin text-lg"></i>
                              ) : (
                                <i className="bi bi-calendar-plus-fill text-xl"></i>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Expandable */}
                    {isExpanded && (
                      <>
                        <div 
                          className="px-3 md:px-6 py-4 md:py-5 max-h-96 overflow-y-auto border-b border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                          onScroll={(e) => e.stopPropagation()}
                        >
                          {/* Column-based Layout */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {/* 1. Basic Information Section */}
                            <div className="bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                              <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                <i className="bi bi-info-circle text-orange-600 text-base"></i>
                                Basic Information
                              </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Reference Number</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.reference_number || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Client Name</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.client_name || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Client Reference</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.client_ref_no || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Operator</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.operator || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Survey Type</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_type || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold flex items-center gap-2">
                                    <span className="text-base text-teal-500">🚢</span>
                                    Vessel Name
                                  </span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.vessel_name || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* 2. Location & Schedule & Notes Section */}
                            <div className="bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                              <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                <i className="bi bi-geo-alt text-orange-600 text-base"></i>
                                Location & Schedule & Notes
                              </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Country</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_country || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Port</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.survey_port || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Notes</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.notes || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">ETA</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.eta || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Appointment Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.appointment_date || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* 3. Surveyor Details Section */}
                            <div className="bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                              <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                <i className="bi bi-person text-orange-600 text-base"></i>
                                Surveyor Details
                              </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Surveyor Type</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.surveyor_type || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Surveyor</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.surveyor_code || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Subcon</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{isSuperuser ? (survey.subcon || '-') : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Subcon Fee Paid</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">
                                    {isSuperuser ? (
                                      survey.subcon_fee_paid ? (
                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">Yes</span>
                                      ) : (
                                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">No</span>
                                      )
                                    ) : (
                                      '-'
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* 4. Operation Dates Section */}
                            <div className="bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                              <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                <i className="bi bi-calendar text-orange-600 text-base"></i>
                                Operation Dates
                              </h4>
                              <div className="space-y-2">
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Attendance Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.attendance_date || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Due Date of Payment</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.due_date_payment || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Received Payment Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{isSuperuser ? (survey.received_payment_date || '-') : '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Initial Report Sent Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.initial_report_sent_date || '-'}</span>
                                </div>
                                <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                  <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Final Report Sent Date</span>
                                  <span className="text-gray-900 text-sm font-medium break-words">{survey.final_report_sent_date || '-'}</span>
                                </div>
                              </div>
                            </div>

                            {/* 5. Bunker Information Section - Full Width */}
                            <div className="col-span-full bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                              <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                <i className="bi bi-fuel-pump text-orange-600 text-base"></i>
                                Bunker Information
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* LSFO */}
                                <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#fdba74' }}>
                                  <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#c2410c', borderColor: '#fdba74' }}>LSFO Details</h5>
                                  <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Logbook</span>
                                      <span className="text-gray-900 font-semibold">{survey.lsfo_logbook || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Survey</span>
                                      <span className="text-gray-900 font-semibold">{survey.lsfo_survey || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                      <span className="text-gray-700 font-semibold">Difference</span>
                                      <span className="font-bold" style={{ color: '#c2410c' }}>{survey.lsfo_qty || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.lsfo_bdn || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.lsfo_vessel || 0} MT</span>
                                    </div>
                                    {isSuperuser && (
                                      <>
                                        <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Price</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.lsfo_price || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Incentive</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.lsfo_recovery || 0}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* HSFO */}
                                <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#fdba74' }}>
                                  <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#c2410c', borderColor: '#fdba74' }}>HSFO Details</h5>
                                  <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Logbook</span>
                                      <span className="text-gray-900 font-semibold">{survey.hsfo_logbook || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Survey</span>
                                      <span className="text-gray-900 font-semibold">{survey.hsfo_survey || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                      <span className="text-gray-700 font-semibold">Difference</span>
                                      <span className="font-bold" style={{ color: '#c2410c' }}>{survey.hsfo_qty || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.hsfo_bdn || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.hsfo_vessel || 0} MT</span>
                                    </div>
                                    {isSuperuser && (
                                      <>
                                        <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Price</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.hsfo_price || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Incentive</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.hsfo_recovery || 0}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* MGO */}
                                <div className="bg-white rounded-xl p-4 border-2 shadow-lg hover:shadow-xl transition-all" style={{ borderColor: '#fdba74' }}>
                                  <h5 className="text-sm mb-4 pb-3 border-b-2 font-bold uppercase tracking-wide" style={{ color: '#c2410c', borderColor: '#fdba74' }}>MGO Details</h5>
                                  <div className="space-y-2.5 text-sm">
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Logbook</span>
                                      <span className="text-gray-900 font-semibold">{survey.mgo_logbook || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Survey</span>
                                      <span className="text-gray-900 font-semibold">{survey.mgo_survey || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-2.5 border-t-2 border-gray-200 py-2 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                      <span className="text-gray-700 font-semibold">Difference</span>
                                      <span className="font-bold" style={{ color: '#c2410c' }}>{survey.mgo_qty || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">BDN Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.mgo_bdn || 0} MT</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-2 bg-gray-50 rounded-lg">
                                      <span className="text-gray-600 font-medium">Vessel Supp. Fig.</span>
                                      <span className="text-gray-900 font-semibold">{survey.mgo_vessel || 0} MT</span>
                                    </div>
                                    {isSuperuser && (
                                      <>
                                        <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300 py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Price</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.mgo_price || 0}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2.5 px-2 rounded-lg" style={{ backgroundColor: '#fff7ed' }}>
                                          <span className="text-gray-700 font-semibold">Incentive</span>
                                          <span className="font-bold" style={{ color: '#c2410c' }}>${survey.mgo_recovery || 0}</span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* 6. Financial Details Section (Superuser only) - Full Width */}
                            {isSuperuser && (
                              <div className="col-span-full bg-[#fffaf5] rounded-xl p-3 border border-[#fed7aa] shadow-sm">
                                <h4 className="text-sm uppercase tracking-wide mb-3 flex items-center gap-1.5 font-bold text-[#c2410c]">
                                  <i className="bi bi-currency-dollar text-orange-600 text-base"></i>
                                  Financial Details
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">PCT Incentive</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">{survey.pct_qty !== null ? `${survey.pct_qty}%` : '-'}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">MT Price</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">{survey.mt_price ? `$${survey.mt_price}` : '-'}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Recovery</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">${survey.recovery || 0}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Survey Fee</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">${survey.survey_fee || 0}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Expenses</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">${survey.expenses || 0}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Discount</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">${survey.discount || 0}</span>
                                  </div>
                                  <div className="bg-white rounded-lg p-2 border border-gray-200 shadow-sm">
                                    <span className="text-gray-500 block mb-0.5 text-sm font-semibold">Others</span>
                                    <span className="text-gray-900 text-sm font-medium break-words">${survey.others || 0}</span>
                                  </div>
                                  <div className="bg-orange-100 rounded-lg p-2 border border-orange-300 shadow-sm">
                                    <span className="text-orange-700 block mb-0.5 text-sm font-semibold">Total</span>
                                    <span className="text-orange-900 text-lg font-medium">${survey.total || 0}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Fixed Bottom Actions */}
                        <div className="px-6 py-4 bg-white border-t border-gray-300 rounded-b-lg">
                          <div className="flex items-center justify-between gap-4">
                            {/* Cancel/Activate Button - Left Side */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault()
                                handleToggleStatus(survey.reference_number, e)
                              }}
                              className={`px-6 py-2.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-2 ${
                                survey.cancelled
                                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow-md'
                                  : 'bg-pink-200 hover:bg-pink-300 text-pink-800 shadow-sm hover:shadow-md'
                              }`}
                            >
                              <i className={`bi ${survey.cancelled ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
                              <span>{survey.cancelled ? 'Activate' : 'Survey Cancel'}</span>
                            </button>

                            {/* Right Side Actions */}
                            <div className="flex items-center gap-4">
                              {/* Upload/Download Report */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault()
                                  setReportsModalRef(survey.reference_number)
                                }}
                                className="px-4 py-2.5 text-sm font-semibold rounded-md bg-premium text-white shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)] transition-shadow flex items-center gap-2"
                              >
                                <i className="bi bi-folder2-open"></i>
                                Upload/Download Report
                              </button>

                              {/* Invoice Sent Toggle */}
                              <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-md border border-gray-300">
                                <span className="text-xs font-medium text-gray-700">
                                  Invoice Sent:
                                </span>
                                <div className="flex items-center gap-1 bg-white rounded border border-gray-300 p-0.5">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleToggleInvoiceSent(survey.reference_number, false, e)
                                    }}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                                      !invoiceSent
                                        ? 'bg-rose-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                                  >
                                    No
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleToggleInvoiceSent(survey.reference_number, true, e)
                                    }}
                                    className={`px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                                      invoiceSent
                                        ? 'bg-emerald-500 text-white'
                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                                  >
                                    Yes
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="bi bi-chevron-left mr-1"></i>
                  Previous
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <i className="bi bi-chevron-right ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {reportsModalRef && (
        <SurveyReportsModal
          referenceNumber={reportsModalRef}
          canUpload={isStaff || isSuperuser}
          onClose={() => setReportsModalRef(null)}
          onChanged={fetchSurveys}
        />
      )}
    </div>
    </>
  )
}

export default SurveyList
